import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  Textarea,
} from '@yeshuman/sdk'
import { useState, useEffect } from 'react'
import { Observation, CreateObservationData, UpdateObservationData } from '../hooks/use-observations'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

interface ObservationFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  observation?: Observation | null
  onCreate: (data: CreateObservationData) => Promise<void>
  onUpdate: (data: UpdateObservationData) => Promise<void>
}

export function ObservationFormDialog({
  open,
  onOpenChange,
  observation,
  onCreate,
  onUpdate,
}: ObservationFormDialogProps) {
  const [text, setText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Initialize form when observation is provided (edit mode)
  useEffect(() => {
    if (observation) {
      setText(observation.text)
    } else {
      // Reset form for create mode
      setText('')
    }
  }, [observation, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!text.trim()) {
      toast.error('Observation text is required')
      return
    }

    setIsSubmitting(true)

    try {
      if (observation) {
        // Update mode
        const updateData: UpdateObservationData = {
          observation_id: observation.id,
          text: text.trim(),
        }

        await onUpdate(updateData)
        toast.success('Observation updated successfully')
      } else {
        // Create mode
        const createData: CreateObservationData = {
          text: text.trim(),
        }

        await onCreate(createData)
        toast.success('Observation recorded successfully')
      }
      
      onOpenChange(false)
    } catch (error: any) {
      toast.error(error.message || 'Failed to save observation')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {observation ? 'Edit Observation' : 'Record Observation'}
          </DialogTitle>
          <DialogDescription>
            {observation 
              ? 'Update your health observation'
              : 'Record a generalized statement about how you\'re feeling or your health state'
            }
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="text">Observation</Label>
            <Textarea
              id="text"
              placeholder="e.g., I've been feeling anxious lately..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              required
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Note: The agent will automatically discover correlations to your recent symptoms and activities.
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !text.trim()}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {observation ? 'Update' : 'Record'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}



